import React, { useState } from 'react';
import { Property, SearchCategory } from '../types';
import { LOCATIONS, PROPERTY_TYPES, BEDROOM_OPTIONS, PLOT_AREA_OPTIONS } from '../constants';
import { listingAPI } from '../services/api';
import AnimatedWrapper from './AnimatedWrapper';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
type OTPStatus = 'idle' | 'sending' | 'sent' | 'verified' | 'error';

const ListPropertyForm: React.FC = () => {
    const [formStatus, setFormStatus] = useState<FormStatus>('idle');
    const [otpStatus, setOtpStatus] = useState<OTPStatus>('idle');
    const [propertyCategory, setPropertyCategory] = useState<SearchCategory>('Residential');
    const [transactionType, setTransactionType] = useState<'Buy' | 'Rent'>('Buy');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [email, setEmail] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [otpCode, setOtpCode] = useState<string>('');
    const [otpMessage, setOtpMessage] = useState<string>('');
    const [countdown, setCountdown] = useState<number>(0);
    const [selectedLocation, setSelectedLocation] = useState<string>('');
    const [customLocation, setCustomLocation] = useState<string>('');

    // Handle OTP countdown timer
    React.useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleRequestOTP = async () => {
        // Validate phone first
        // const phone = document.querySelector<HTMLInputElement>('input[name="phone"]')?.value; // No longer needed
        const phoneRegex = /^[6-9]\d{9}$/;

        if (!phone || !phoneRegex.test(phone.replace(/\s+/g, ''))) {
            const newErrors = { ...errors };
            newErrors['phone'] = 'Please enter a valid 10-digit mobile number';
            setErrors(newErrors);
            return;
        }

        // Clear phone error
        const newErrors = { ...errors };
        delete newErrors['phone'];
        setErrors(newErrors);

        setOtpStatus('sending');
        setOtpMessage('');

        // Ensure phone number has country code if not present
        // Depending on backend requirement. The test script used +91.
        // Let's assume user enters 10 digits and we append +91 or backend handles it.
        // Ideally backend handles it, but let's ensure we send a clean number.
        const cleanPhone = phone.replace(/\s+/g, '');
        // We will send the number as is, assuming backend expects 10 digits or handles +91. 
        // Looking at backend `validatePhone` in `utils/helpers.js` would confirm, but usually keeping it simple is best.
        // Actually, let's prepend +91 for India if length is 10, just to be safe if backend expects E.164
        const formattedPhone = cleanPhone.length === 10 ? `+91${cleanPhone}` : cleanPhone;

        try {
            const result = await listingAPI.requestOTP(formattedPhone);

            if (result.success) {
                setOtpStatus('sent');
                setOtpMessage(result.message);
                setCountdown(120); // 2 minute cooldown
            } else {
                setOtpStatus('error');
                setOtpMessage(result.message);
            }
        } catch (error) {
            setOtpStatus('error');
            setOtpMessage('Failed to send OTP. Please try again.');
        }
    };

    const handleVerifyOTP = async () => {
        if (!otpCode || otpCode.length !== 6) {
            setErrors({ ...errors, otp: 'Please enter a valid 6-digit OTP' });
            return;
        }

        const cleanPhone = phone.replace(/\s+/g, '');
        const formattedPhone = cleanPhone.length === 10 ? `+91${cleanPhone}` : cleanPhone;

        try {
            const result = await listingAPI.verifyOTP(formattedPhone, otpCode);

            if (result.success) {
                setOtpStatus('verified');
                setOtpMessage('OTP verified successfully');
                const newErrors = { ...errors };
                delete newErrors['otp'];
                setErrors(newErrors);
            } else {
                setErrors({ ...errors, otp: result.message });
            }
        } catch (error) {
            setErrors({ ...errors, otp: 'Failed to verify OTP' });
        }
    };

    const validateForm = (formData: FormData): boolean => {
        const newErrors: Record<string, string> = {};

        // Validate name
        const fullName = formData.get('full-name') as string;
        if (!fullName || fullName.trim().length < 2) {
            newErrors['full-name'] = 'Please enter a valid full name';
        }

        // Validate email (use state variable since it's a controlled input)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            newErrors['email'] = 'Please enter a valid email address';
        }

        // Validate OTP
        if (!otpCode || otpCode.length !== 6) {
            newErrors['otp'] = 'Please enter the 6-digit OTP sent to your WhatsApp';
        }

        // Check if OTP was sent
        if (otpStatus !== 'sent' && otpStatus !== 'verified') {
            newErrors['otp'] = 'Please request and verify OTP first';
        }

        // Validate phone
        // Use state 'phone' because the input might be disabled (verified) and thus not in formData
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phone || !phoneRegex.test(phone.replace(/\s+/g, ''))) {
            newErrors['phone'] = 'Please enter a valid 10-digit mobile number';
        }

        // Validate property title
        const propertyTitle = formData.get('property-title') as string;
        if (!propertyTitle || propertyTitle.trim().length < 5) {
            newErrors['property-title'] = 'Property title must be at least 5 characters';
        }

        // Validate price
        const price = formData.get('price') as string;
        if (!price || price.trim().length === 0) {
            newErrors['price'] = 'Please enter the expected price';
        }

        // Validate area
        const area = formData.get('residential-area') || formData.get('commercial-area') || formData.get('area');
        // If area is "More than 50", it's valid. If it's a number, it must be > 0.
        if (!area) {
            newErrors['area'] = 'Please enter a valid area';
        } else if (area !== 'More than 50' && Number(area) <= 0) {
            newErrors['area'] = 'Please enter a valid area';
        }

        // Validate description
        const description = formData.get('description') as string;
        // Validate custom location
        if (selectedLocation === 'Other' && (!customLocation || customLocation.trim().length < 2)) {
            newErrors['location'] = 'Please enter a valid location';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrors({});

        const formData = new FormData(e.currentTarget);

        // Validate form
        if (!validateForm(formData)) {
            setFormStatus('error');
            return;
        }

        const cleanPhone = phone.replace(/\s+/g, '');

        setFormStatus('submitting');
        const data = {
            fullName: formData.get('full-name'),
            email: email, // Use state variable for controlled input
            phone: cleanPhone.length === 10 ? `+91${cleanPhone}` : cleanPhone, // Use the formatted phone we verify
            propertyCategory,
            transactionType,
            propertyTitle: formData.get('property-title'),
            location: selectedLocation === 'Other' ? customLocation : selectedLocation,
            price: formData.get('price'),
            propertyType: formData.get('property-type'),
            bedrooms: formData.get('bedrooms'),
            area: formData.get('residential-area') || formData.get('commercial-area') || formData.get('area'),
            areaUnit: propertyCategory === 'Plots' ? 'cents' : 'sqft',
            plotType: formData.get('plot-type'),
            commercialType: formData.get('commercial-type'),
            description: formData.get('description'),
            otpCode: otpCode
        };

        try {
            const result = await listingAPI.submit({
                fullName: String(data.fullName),
                email: String(data.email),
                phone: String(data.phone), // Ensure we send the verified phone format
                propertyCategory: data.propertyCategory,
                transactionType: data.transactionType,
                propertyTitle: String(data.propertyTitle),
                location: String(data.location),
                price: String(data.price),
                propertyType: data.propertyType ? String(data.propertyType) : undefined,
                bedrooms: data.bedrooms ? String(data.bedrooms) : undefined,
                area: data.area && data.area !== 'More than 50' ? Number(data.area) : undefined, // Handle "More than 50" or numbers
                // If "More than 50" is selected, we might need to pass it differently or the backend needs to handle string area.
                // Assuming backend expects number, we might send 51 or just undefined and handle it in description or another field if strictly typed.
                // For now, sending undefined if not a number to avoid crash, but ideally backend should support string or range.
                // However, user asked for UI change. I'll keep it safe.
                areaUnit: data.areaUnit,
                plotType: data.plotType ? String(data.plotType) : undefined,
                commercialType: data.commercialType ? String(data.commercialType) : undefined,
                description: data.description ? String(data.description) : undefined,
                otpCode: data.otpCode
            });

            if (result.success) {
                setFormStatus('success');
                setOtpStatus('verified');
            } else {
                setFormStatus('error');
                setErrors({ ...errors, 'submit': result.message });
            }
        } catch (error) {
            console.error('Error submitting listing:', error);
            setFormStatus('error');
        }
    };

    if (formStatus === 'success') {
        return (
            <section className="bg-white py-12">
                <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-16 text-center">
                    <AnimatedWrapper>
                        <h2 className="text-3xl font-semibold text-green-600">Thank You!</h2>
                        <p className="mt-4 text-lg text-gray-600">Your property details have been submitted successfully. Our team will review it and get in touch with you shortly.</p>
                    </AnimatedWrapper>
                </div>
            </section>
        )
    }

    const renderPropertyFields = () => {
        switch (propertyCategory) {
            case 'Plots':
                return (
                    <>
                        <div className="sm:col-span-3">
                            <label htmlFor="area" className="block text-sm font-medium leading-6 text-gray-900">Area (in cents)</label>
                            <div className="mt-2">
                                <select id="area" name="area" required className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6">
                                    <option value="" disabled>Select Area</option>
                                    {PLOT_AREA_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="sm:col-span-3">
                            <label htmlFor="plot-type" className="block text-sm font-medium leading-6 text-gray-900">Plot Type</label>
                            <div className="mt-2">
                                <select id="plot-type" name="plot-type" required className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6">
                                    <option>Residential</option>
                                    <option>Commercial</option>
                                </select>
                            </div>
                        </div>
                    </>
                );
            case 'Commercial':
                return (
                    <>
                        <div className="sm:col-span-3">
                            <label htmlFor="commercial-type" className="block text-sm font-medium leading-6 text-gray-900">Property Type</label>
                            <div className="mt-2">
                                <select id="commercial-type" name="commercial-type" required className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6">
                                    <option>Office Space</option>
                                    <option>Retail Shop</option>
                                    <option>Warehouse</option>
                                    <option>Full Building</option>
                                </select>
                            </div>
                        </div>
                        <div className="sm:col-span-3">
                            <label htmlFor="commercial-area" className="block text-sm font-medium leading-6 text-gray-900">Area (in sqft)</label>
                            <div className="mt-2">
                                <input type="number" name="commercial-area" id="commercial-area" required className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6" />
                            </div>
                        </div>
                    </>
                );
            case 'Residential':
            default:
                return (
                    <>
                        <div className="sm:col-span-2">
                            <label htmlFor="property-type" className="block text-sm font-medium leading-6 text-gray-900">Property Type</label>
                            <div className="mt-2">
                                <select id="property-type" name="property-type" required className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6">
                                    {PROPERTY_TYPES.map(type => <option key={type}>{type}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="bedrooms" className="block text-sm font-medium leading-6 text-gray-900">Bedrooms (BHK)</label>
                            <div className="mt-2">
                                <select id="bedrooms" name="bedrooms" required className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6">
                                    {BEDROOM_OPTIONS.map(opt => <option key={opt}>{opt} BHK</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="residential-area" className="block text-sm font-medium leading-6 text-gray-900">Area (in sqft)</label>
                            <div className="mt-2">
                                <input type="number" name="residential-area" id="residential-area" required className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6" />
                            </div>
                        </div>
                    </>
                );
        }
    }

    return (
        <section className="bg-white" id="list-property-form">
            <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-12">
                <AnimatedWrapper>
                    <div className="text-center mb-8">
                        <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
                            List Your Property Details
                        </h2>
                        <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg text-gray-500">
                            Provide the details below and our team will get in touch with you to begin the verification process.
                        </p>
                    </div>
                </AnimatedWrapper>
                <form onSubmit={handleSubmit} className="max-w-4xl mx-auto bg-gray-50 p-8 rounded-2xl shadow-sm border border-gray-200">
                    <div className="space-y-12">
                        {/* Contact Information Section */}
                        <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-xl font-semibold leading-7 text-gray-900">Your Contact Details</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">This information will be used to contact you about your listing.</p>

                            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <label htmlFor="full-name" className="block text-sm font-medium leading-6 text-gray-900">Full Name</label>
                                    <div className="mt-2">
                                        <input type="text" name="full-name" id="full-name" autoComplete="name" required className={`block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ${errors['full-name'] ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6`} />
                                        {errors['full-name'] && <p className="mt-1 text-sm text-red-600">{errors['full-name']}</p>}
                                    </div>
                                </div>

                                <div className="sm:col-span-3">
                                    <label htmlFor="phone" className="block text-sm font-medium leading-6 text-gray-900">WhatsApp Number</label>
                                    <div className="mt-2 flex gap-3">
                                        <div className="relative w-full">
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-sm pointer-events-none">
                                                +91
                                            </span>
                                            <input
                                                type="tel"
                                                name="phone"
                                                id="phone"
                                                autoComplete="tel"
                                                required
                                                placeholder="9876543210"
                                                maxLength={10}
                                                value={phone}
                                                disabled={otpStatus === 'sent' || otpStatus === 'verified'}
                                                className={`block w-full rounded-md border-0 py-2.5 pl-12 text-gray-900 shadow-sm ring-1 ring-inset ${errors['phone'] ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                                onChange={(e) => {
                                                    // Allow only numbers
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    setPhone(val);
                                                    if (errors['phone']) {
                                                        const newErrors = { ...errors };
                                                        delete newErrors['phone'];
                                                        setErrors(newErrors);
                                                    }
                                                }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRequestOTP}
                                            disabled={otpStatus === 'sending' || otpStatus === 'sent' || countdown > 0}
                                            className="whitespace-nowrap rounded-md bg-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            {otpStatus === 'sending' ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Sending...
                                                </>
                                            ) : otpStatus === 'sent' ? (
                                                countdown > 0 ? `Resend (${countdown}s)` : 'Resend OTP'
                                            ) : (
                                                'Get OTP'
                                            )}
                                        </button>
                                    </div>
                                    {errors['phone'] && <p className="mt-1 text-sm text-red-600">{errors['phone']}</p>}
                                    {otpMessage && (
                                        <p className={`mt-2 text-sm ${(otpStatus === 'sent' || otpStatus === 'verified') ? 'text-green-600' : 'text-red-600'}`}>
                                            {(otpStatus === 'sent' || otpStatus === 'verified') && '✓ '}{otpMessage}
                                        </p>
                                    )}
                                </div>

                                <div className="sm:col-span-3">
                                    <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">Email address</label>
                                    <div className="mt-2">
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                if (errors['email']) {
                                                    const newErrors = { ...errors };
                                                    delete newErrors['email'];
                                                    setErrors(newErrors);
                                                }
                                            }}
                                            className={`block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ${errors['email'] ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6`}
                                        />
                                    </div>
                                    {errors['email'] && <p className="mt-1 text-sm text-red-600">{errors['email']}</p>}
                                </div>

                                {(otpStatus === 'sent' || otpStatus === 'verified') && (
                                    <div className="sm:col-span-full">
                                        <label htmlFor="otp" className="block text-sm font-medium leading-6 text-gray-900">
                                            Enter OTP
                                            <span className="ml-2 text-xs text-gray-500">(Check your WhatsApp for 6-digit code)</span>
                                        </label>
                                        <div className="mt-2 flex gap-3">
                                            <input
                                                id="otp"
                                                name="otp"
                                                type="text"
                                                maxLength={6}
                                                pattern="[0-9]{6}"
                                                placeholder="Enter 6-digit OTP"
                                                required
                                                value={otpCode}
                                                disabled={otpStatus === 'verified'}
                                                onChange={(e) => {
                                                    setOtpCode(e.target.value.replace(/\D/g, ''));
                                                    if (errors['otp']) {
                                                        const newErrors = { ...errors };
                                                        delete newErrors['otp'];
                                                        setErrors(newErrors);
                                                    }
                                                }}
                                                className={`block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ${errors['otp'] ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6 tracking-widest text-center text-lg font-semibold disabled:bg-gray-100 disabled:cursor-not-allowed`}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleVerifyOTP}
                                                disabled={otpCode.length !== 6 || otpStatus === 'verified'}
                                                className="whitespace-nowrap rounded-md bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:bg-gray-400 disabled:cursor-not-allowed"
                                            >
                                                {otpStatus === 'verified' ? 'Verified ✓' : 'Verify'}
                                            </button>
                                        </div>
                                        {errors['otp'] && <p className="mt-1 text-sm text-red-600">{errors['otp']}</p>}
                                        <p className="mt-2 text-xs text-gray-500">
                                            ⏱️ OTP is valid for 10 minutes. Didn't receive it? Check spam folder or request a new one.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Property Information Section */}
                        <div className="pb-12">
                            <h2 className="text-xl font-semibold leading-7 text-gray-900">Property Information</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">Select the property type and fill in the details.</p>

                            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <label htmlFor="property-category" className="block text-sm font-medium leading-6 text-gray-900">Property Category</label>
                                    <div className="mt-2">
                                        <select id="property-category" name="property-category" value={propertyCategory} onChange={(e) => setPropertyCategory(e.target.value as SearchCategory)} className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6">
                                            <option>Residential</option>
                                            <option>Plots</option>
                                            <option>Commercial</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="sm:col-span-3">
                                    <label htmlFor="transaction-type" className="block text-sm font-medium leading-6 text-gray-900">For (Rent/Sale)</label>
                                    <div className="mt-2">
                                        <select
                                            id="transaction-type"
                                            name="transaction-type"
                                            value={transactionType}
                                            onChange={(e) => setTransactionType(e.target.value as 'Buy' | 'Rent')}
                                            className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6"
                                        >
                                            <option value="Buy">Sale</option>
                                            <option value="Rent">Rent</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="sm:col-span-full">
                                    <label htmlFor="property-title" className="block text-sm font-medium leading-6 text-gray-900">Property Title</label>
                                    <div className="mt-2">
                                        <input type="text" name="property-title" id="property-title" required placeholder="e.g., 3 BHK Apartment in Marine Drive" className={`block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ${errors['property-title'] ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6`} />
                                        {errors['property-title'] && <p className="mt-1 text-sm text-red-600">{errors['property-title']}</p>}
                                    </div>
                                </div>

                                <div className="sm:col-span-3">
                                    <label htmlFor="location" className="block text-sm font-medium leading-6 text-gray-900">Location</label>
                                    <div className="mt-2">
                                        <select
                                            id="location"
                                            name="location"
                                            required
                                            value={selectedLocation}
                                            onChange={(e) => setSelectedLocation(e.target.value)}
                                            className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6"
                                        >
                                            <option value="" disabled>Select Location</option>
                                            {LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    {selectedLocation === 'Other' && (
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                name="custom-location"
                                                placeholder="Enter your location"
                                                required
                                                value={customLocation}
                                                onChange={(e) => setCustomLocation(e.target.value)}
                                                className={`block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ${errors['location'] ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6`}
                                            />
                                            {errors['location'] && <p className="mt-1 text-sm text-red-600">{errors['location']}</p>}
                                        </div>
                                    )}
                                </div>

                                <div className="sm:col-span-3">
                                    <label htmlFor="price" className="block text-sm font-medium leading-6 text-gray-900">Expected Price (in ₹)</label>
                                    <div className="mt-2">
                                        <input type="text" name="price" id="price" required placeholder="e.g., 1.2 Cr or 85 L" className={`block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ${errors['price'] ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6`} />
                                        {errors['price'] && <p className="mt-1 text-sm text-red-600">{errors['price']}</p>}
                                    </div>
                                </div>

                                {renderPropertyFields()}

                                <div className="col-span-full">
                                    <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">Description</label>
                                    <div className="mt-2">
                                        <textarea id="description" name="description" rows={4} required className={`block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ${errors['description'] ? 'ring-red-500' : 'ring-gray-300'} placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary sm:text-sm sm:leading-6`}></textarea>
                                        {errors['description'] && <p className="mt-1 text-sm text-red-600">{errors['description']}</p>}
                                    </div>
                                    <p className="mt-3 text-sm leading-6 text-gray-600">Write a few sentences about your property.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {errors['submit'] && (
                        <div className="mt-6 rounded-md bg-red-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-red-800">{errors['submit']}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex items-center justify-end gap-x-6">
                        <button type="button" className="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
                        <button
                            type="submit"
                            disabled={formStatus === 'submitting' || otpStatus !== 'verified'}
                            className="rounded-md bg-brand-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {formStatus === 'submitting' ? 'Submitting...' : 'Request Listing'}
                        </button>
                    </div>

                    {otpStatus !== 'verified' && (
                        <p className="mt-4 text-center text-sm text-gray-500">
                            Please verify your WhatsApp number with OTP before submitting the form
                        </p>
                    )}
                </form>
            </div>
        </section>
    );
};

export default ListPropertyForm;